/**
 * Style resolver for V2 — walks the full OOXML style precedence chain
 * and returns fully resolved properties in raw OOXML units.
 *
 * Unlike V1 which resolves styles lazily during node construction,
 * V2 resolves all styles upfront before IR construction begins.
 */

import {
  ResolvedRunProperties,
  ResolvedParagraphProperties,
  ResolvedStyleData,
} from '../irTypes';
import {
  getChildNS,
  W_NS,
  highlightColorToHex,
} from '../../ooxmlParser/zipReader';
import type { ParseContext } from '../../ooxmlParser/types';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve a style to its full properties by walking the basedOn chain.
 * Returns properties with raw OOXML units (twips, half-points).
 */
export function resolveStylePropertiesRaw(
  styleId: string,
  ctx: ParseContext,
  visited = new Set<string>()
): ResolvedStyleData {
  const result: ResolvedStyleData = {
    runProperties: {},
    paragraphProperties: {},
  };

  const style = ctx.styles.get(styleId);
  if (!style || visited.has(styleId)) return result;
  visited.add(styleId);

  // First resolve parent style
  if (style.basedOn) {
    const parent = resolveStylePropertiesRaw(style.basedOn, ctx, visited);
    Object.assign(result.runProperties, parent.runProperties);
    Object.assign(result.paragraphProperties, parent.paragraphProperties);
  }

  // Then overlay this style's run properties (in raw units)
  if (style.runProperties) {
    const rp = style.runProperties;
    if (rp.bold !== undefined) result.runProperties.bold = rp.bold;
    if (rp.italic !== undefined) result.runProperties.italic = rp.italic;
    if (rp.underline !== undefined) result.runProperties.underline = rp.underline;
    if (rp.strike !== undefined) result.runProperties.strike = rp.strike;
    if (rp.subscript !== undefined) result.runProperties.subscript = rp.subscript;
    if (rp.superscript !== undefined) result.runProperties.superscript = rp.superscript;
    if (rp.color !== undefined) result.runProperties.color = rp.color;
    if (rp.highlight !== undefined) result.runProperties.highlight = rp.highlight;
    // V1 stores fontSize as points (half-points / 2). Convert back to raw half-points for IR.
    if (rp.fontSize !== undefined) result.runProperties.fontSizeHalfPt = rp.fontSize * 2;
    if (rp.fontFamily !== undefined) result.runProperties.fontFamily = rp.fontFamily;
  }

  // Overlay paragraph properties (convert V1's points back to raw twips)
  if (style.paragraphProperties) {
    const pp = style.paragraphProperties;
    if (pp.styleId !== undefined) result.paragraphProperties.styleId = pp.styleId;
    if (pp.alignment !== undefined) result.paragraphProperties.alignment = pp.alignment;
    if (pp.outlineLevel !== undefined) result.paragraphProperties.outlineLevel = pp.outlineLevel;
    if (pp.numId !== undefined) result.paragraphProperties.numId = pp.numId;
    if (pp.numLevel !== undefined) result.paragraphProperties.numLevel = pp.numLevel;
    // V1 stores spacing in points — convert back to twips for IR
    if (pp.spaceBefore !== undefined) result.paragraphProperties.spaceBeforeTwips = Math.round(pp.spaceBefore * 20);
    if (pp.spaceAfter !== undefined) result.paragraphProperties.spaceAfterTwips = Math.round(pp.spaceAfter * 20);
    if (pp.lineSpacing !== undefined) {
      result.paragraphProperties.lineSpacing = { value: Math.round(pp.lineSpacing * 240), rule: 'auto' };
    }
    if (pp.leftIndent !== undefined) result.paragraphProperties.leftIndentTwips = Math.round(pp.leftIndent * 20);
    if (pp.rightIndent !== undefined) result.paragraphProperties.rightIndentTwips = Math.round(pp.rightIndent * 20);
    if (pp.firstLineIndent !== undefined) result.paragraphProperties.firstLineIndentTwips = Math.round(pp.firstLineIndent * 20);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Raw property extraction from XML elements (V2 versions — no unit conversion)
// ---------------------------------------------------------------------------

/**
 * Parse <w:rPr> element into raw run properties (half-points, no conversion).
 * This is V2's version — keeps raw OOXML units instead of converting to points.
 */
export function parseRunPropertiesRaw(rPr: Element): ResolvedRunProperties {
  const props: ResolvedRunProperties = {};

  if (isToggleOn(getChildNS(rPr, W_NS, 'b'))) props.bold = true;
  if (isToggleOn(getChildNS(rPr, W_NS, 'i'))) props.italic = true;

  const uEl = getChildNS(rPr, W_NS, 'u');
  if (uEl) {
    const uVal = uEl.getAttributeNS(W_NS, 'val') || uEl.getAttribute('w:val') || '';
    if (uVal !== 'none') props.underline = true;
  }

  if (isToggleOn(getChildNS(rPr, W_NS, 'strike'))) props.strike = true;

  const vertAlignEl = getChildNS(rPr, W_NS, 'vertAlign');
  if (vertAlignEl) {
    const val = vertAlignEl.getAttributeNS(W_NS, 'val') || vertAlignEl.getAttribute('w:val');
    if (val === 'subscript') props.subscript = true;
    if (val === 'superscript') props.superscript = true;
  }

  const colorEl = getChildNS(rPr, W_NS, 'color');
  if (colorEl) {
    const val = colorEl.getAttributeNS(W_NS, 'val') || colorEl.getAttribute('w:val');
    if (val && val !== 'auto') props.color = val;
  }

  const highlightEl = getChildNS(rPr, W_NS, 'highlight');
  if (highlightEl) {
    const val = highlightEl.getAttributeNS(W_NS, 'val') || highlightEl.getAttribute('w:val');
    if (val) props.highlight = highlightColorToHex(val);
  }

  const szEl = getChildNS(rPr, W_NS, 'sz');
  if (szEl) {
    const val = szEl.getAttributeNS(W_NS, 'val') || szEl.getAttribute('w:val');
    if (val) props.fontSizeHalfPt = parseInt(val, 10); // Raw half-points, NOT divided by 2
  }

  const fontEl = getChildNS(rPr, W_NS, 'rFonts');
  if (fontEl) {
    const font =
      fontEl.getAttributeNS(W_NS, 'ascii') ||
      fontEl.getAttribute('w:ascii') ||
      fontEl.getAttributeNS(W_NS, 'eastAsia') ||
      fontEl.getAttribute('w:eastAsia') ||
      fontEl.getAttributeNS(W_NS, 'hAnsi') ||
      fontEl.getAttribute('w:hAnsi');
    if (font) props.fontFamily = font;
  }

  return props;
}

/**
 * Parse <w:pPr> element into raw paragraph properties (twips, no conversion).
 * This is V2's version — keeps raw OOXML units.
 */
export function parseParagraphPropertiesRaw(pPr: Element): ResolvedParagraphProperties {
  const props: ResolvedParagraphProperties = {};

  const pStyleEl = getChildNS(pPr, W_NS, 'pStyle');
  if (pStyleEl) {
    props.styleId = pStyleEl.getAttributeNS(W_NS, 'val') || pStyleEl.getAttribute('w:val') || undefined;
  }

  const jcEl = getChildNS(pPr, W_NS, 'jc');
  if (jcEl) {
    const val = jcEl.getAttributeNS(W_NS, 'val') || jcEl.getAttribute('w:val');
    if (val === 'left' || val === 'center' || val === 'right' || val === 'both') {
      props.alignment = val;
    }
  }

  const outlineLvlEl = getChildNS(pPr, W_NS, 'outlineLvl');
  if (outlineLvlEl) {
    const val = outlineLvlEl.getAttributeNS(W_NS, 'val') || outlineLvlEl.getAttribute('w:val');
    if (val !== null && val !== undefined) props.outlineLevel = parseInt(val, 10);
  }

  // Numbering
  const numPr = getChildNS(pPr, W_NS, 'numPr');
  if (numPr) {
    const numIdEl = getChildNS(numPr, W_NS, 'numId');
    if (numIdEl) {
      props.numId = numIdEl.getAttributeNS(W_NS, 'val') || numIdEl.getAttribute('w:val') || undefined;
    }
    const ilvlEl = getChildNS(numPr, W_NS, 'ilvl');
    if (ilvlEl) {
      const val = ilvlEl.getAttributeNS(W_NS, 'val') || ilvlEl.getAttribute('w:val');
      if (val !== null && val !== undefined) props.numLevel = parseInt(val, 10);
    }
  }

  // Spacing — raw twips
  const spacingEl = getChildNS(pPr, W_NS, 'spacing');
  if (spacingEl) {
    const before = spacingEl.getAttributeNS(W_NS, 'before') || spacingEl.getAttribute('w:before');
    if (before) props.spaceBeforeTwips = parseInt(before, 10);

    const after = spacingEl.getAttributeNS(W_NS, 'after') || spacingEl.getAttribute('w:after');
    if (after) props.spaceAfterTwips = parseInt(after, 10);

    const line = spacingEl.getAttributeNS(W_NS, 'line') || spacingEl.getAttribute('w:line');
    const lineRule = spacingEl.getAttributeNS(W_NS, 'lineRule') || spacingEl.getAttribute('w:lineRule');
    if (line) {
      const lineVal = parseInt(line, 10);
      const rule = (lineRule === 'exact' || lineRule === 'atLeast') ? lineRule : 'auto';
      props.lineSpacing = { value: lineVal, rule };
    }
  }

  // Indentation — raw twips
  const indEl = getChildNS(pPr, W_NS, 'ind');
  if (indEl) {
    const left = indEl.getAttributeNS(W_NS, 'left') || indEl.getAttribute('w:left');
    if (left) props.leftIndentTwips = parseInt(left, 10);

    const right = indEl.getAttributeNS(W_NS, 'right') || indEl.getAttribute('w:right');
    if (right) props.rightIndentTwips = parseInt(right, 10);

    const firstLine = indEl.getAttributeNS(W_NS, 'firstLine') || indEl.getAttribute('w:firstLine');
    if (firstLine) props.firstLineIndentTwips = parseInt(firstLine, 10);

    const hanging = indEl.getAttributeNS(W_NS, 'hanging') || indEl.getAttribute('w:hanging');
    if (hanging) props.firstLineIndentTwips = -parseInt(hanging, 10);
  }

  return props;
}

/**
 * Merge explicit run properties on top of resolved style run properties.
 * Explicit values override style values.
 */
export function mergeRunProperties(
  style: ResolvedRunProperties,
  explicit: ResolvedRunProperties
): ResolvedRunProperties {
  const merged: ResolvedRunProperties = { ...style };
  if (explicit.bold !== undefined) merged.bold = explicit.bold;
  if (explicit.italic !== undefined) merged.italic = explicit.italic;
  if (explicit.underline !== undefined) merged.underline = explicit.underline;
  if (explicit.strike !== undefined) merged.strike = explicit.strike;
  if (explicit.subscript !== undefined) merged.subscript = explicit.subscript;
  if (explicit.superscript !== undefined) merged.superscript = explicit.superscript;
  if (explicit.color !== undefined) merged.color = explicit.color;
  if (explicit.highlight !== undefined) merged.highlight = explicit.highlight;
  if (explicit.fontSizeHalfPt !== undefined) merged.fontSizeHalfPt = explicit.fontSizeHalfPt;
  if (explicit.fontFamily !== undefined) merged.fontFamily = explicit.fontFamily;
  return merged;
}

/**
 * Merge explicit paragraph properties on top of resolved style paragraph properties.
 */
export function mergeParagraphProperties(
  style: ResolvedParagraphProperties,
  explicit: ResolvedParagraphProperties
): ResolvedParagraphProperties {
  const merged: ResolvedParagraphProperties = { ...style };
  if (explicit.alignment !== undefined) merged.alignment = explicit.alignment;
  if (explicit.outlineLevel !== undefined) merged.outlineLevel = explicit.outlineLevel;
  if (explicit.numId !== undefined) merged.numId = explicit.numId;
  if (explicit.numLevel !== undefined) merged.numLevel = explicit.numLevel;
  if (explicit.spaceBeforeTwips !== undefined) merged.spaceBeforeTwips = explicit.spaceBeforeTwips;
  if (explicit.spaceAfterTwips !== undefined) merged.spaceAfterTwips = explicit.spaceAfterTwips;
  if (explicit.lineSpacing !== undefined) merged.lineSpacing = explicit.lineSpacing;
  if (explicit.leftIndentTwips !== undefined) merged.leftIndentTwips = explicit.leftIndentTwips;
  if (explicit.rightIndentTwips !== undefined) merged.rightIndentTwips = explicit.rightIndentTwips;
  if (explicit.firstLineIndentTwips !== undefined) merged.firstLineIndentTwips = explicit.firstLineIndentTwips;
  return merged;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Check if a toggle element is "on" (OOXML toggle convention). */
function isToggleOn(el: Element | null): boolean {
  if (!el) return false;
  const val = el.getAttributeNS(W_NS, 'val') || el.getAttribute('w:val');
  if (val === '0' || val === 'false') return false;
  return true;
}
